import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, KeyRound, X } from 'lucide-react';
import { getLicenseStatus } from '../api/l1';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import { ActivationRequestForm, InstallLicenseForm } from '../pages/LicenseManagement';
import {
  computeLicenseBannerStatus,
  TENANT_MISMATCH_MESSAGE,
  type LicenseBannerStatus,
  type LicenseGateReason,
  type LicenseStatusPayload,
} from './licenseGateLogic';

// Replaces RAM-215's mandatory, blocking LicenseGate.tsx (deleted -- LicenseGateModal has no
// callers left once RequireAuth.tsx stops rendering it). Per direct product-owner direction
// (2026-08-08, "non-blocking banner"): the app is reachable regardless of license state; this is
// a persistent-but-dismissible strip mounted once in Layout.tsx's header status cluster, not a
// gate anything renders in place of.
//
// Query key is deliberately the SAME `['license-status']` key LicenseManagement.tsx's own
// `getLicenseStatus` query uses (not a banner-private key) -- carried over unchanged from
// LicenseGate.tsx's own reasoning: shares cache with the License Management page, and an install
// performed from inside this banner's popover invalidates the one cache entry that page also reads.
const LICENSE_STATUS_QUERY_KEY = ['license-status'];

// Session-scoped dismissal, keyed by the exact status content dismissed -- a *different* problem
// (or the same problem coming back after being fixed) is a different key and reappears even if
// the banner was dismissed earlier this session. AuthContext's logout() also clears this key
// outright, so "next login" always starts fresh per the design's "never silently vanish forever"
// requirement. NOTE: AuthContext.tsx duplicates this literal string rather than importing it, to
// avoid a circular import (this file already imports useAuth from AuthContext) -- keep both in
// sync if this ever changes.
export const LICENSE_BANNER_DISMISS_KEY = 'rtf_license_banner_dismissed';

function dismissalToken(status: LicenseBannerStatus, rawStatus?: string): string {
  return `${status}:${rawStatus ?? ''}`;
}

export function useLicenseBannerStatus() {
  const { isAuthenticated, user } = useAuth();
  const authSource = user?.authSource;
  // Local-admin sessions skip this entirely -- no request is even made, matching the design's
  // "never show this banner to the one identity that must always be able to install a license
  // without any noise" rule.
  const enabled = isAuthenticated && authSource !== 'local';

  const { data, isLoading, isError } = useQuery({
    queryKey: LICENSE_STATUS_QUERY_KEY,
    queryFn: () => getLicenseStatus().then((r) => r.data as LicenseStatusPayload),
    enabled,
    staleTime: 60_000,
  });

  const outcome = computeLicenseBannerStatus({
    authSource,
    tenantName: user?.tenantName,
    tenantRole: user?.tenantRole,
    data,
    isLoading,
    isError,
  });

  return {
    loading: enabled && isLoading,
    status: outcome.status,
    rawStatus: outcome.rawStatus,
  };
}

function humanizeStatus(status: string): string {
  return status.replace(/_/g, ' ');
}

const SEVERITY_STYLES: Record<LicenseGateReason, { bg: string; border: string; text: string }> = {
  not_installed: { bg: '#fffbeb', border: '#fbbf24', text: '#92400e' },
  invalid_status: { bg: '#fef2f2', border: '#f87171', text: '#991b1b' },
  tenant_mismatch: { bg: '#fef2f2', border: '#f87171', text: '#991b1b' },
};

function messageFor(status: LicenseGateReason, rawStatus?: string): string {
  if (status === 'tenant_mismatch') return TENANT_MISMATCH_MESSAGE;
  if (status === 'not_installed') return 'This deployment has no license installed yet.';
  return `This deployment's license is currently ${humanizeStatus(rawStatus || 'invalid')}.`;
}

// Sec 12.1 (carried over from RAM-215's design doc): license installation must never be a dead
// end -- true here too, just via a popover instead of a full-screen block. Reuses
// LicenseManagement.tsx's already-exported forms as-is rather than duplicating either flow.
function GetLicensedModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [showActivation, setShowActivation] = useState(false);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: LICENSE_STATUS_QUERY_KEY });

  return (
    <Modal title="Get Licensed" open={open} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <button
            type="button"
            className="btn-secondary text-xs w-full justify-center"
            onClick={() => setShowActivation((v) => !v)}
          >
            {showActivation ? 'Hide activation request' : 'Generate Activation Request'}
          </button>
          {showActivation && (
            <div className="border border-slate-200 rounded-lg p-3 mt-3">
              <ActivationRequestForm onClose={() => setShowActivation(false)} />
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 pt-4">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Install License
          </div>
          <InstallLicenseForm onInstalled={() => { invalidate(); onClose(); }} />
        </div>
      </div>
    </Modal>
  );
}

export default function LicenseStatusBanner() {
  const { status, rawStatus, loading } = useLicenseBannerStatus();
  const [showGetLicensed, setShowGetLicensed] = useState(false);
  const [dismissed, setDismissed] = useState<string | null>(null);

  useEffect(() => {
    setDismissed(sessionStorage.getItem(LICENSE_BANNER_DISMISS_KEY));
  }, []);

  // Nothing to show: still loading, local admin, or a genuinely fine (ACTIVE/GRACE, matching
  // tenant) license -- see computeLicenseBannerStatus's 'ok' cases for exactly which those are.
  if (loading || status === 'ok') return null;

  const token = dismissalToken(status, rawStatus);
  if (dismissed === token) return null;

  const styles = SEVERITY_STYLES[status];

  return (
    <>
      <div
        role="status"
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '5px 10px', borderRadius: 8,
          backgroundColor: styles.bg, border: `1.5px solid ${styles.border}`,
          color: styles.text, fontSize: 11, fontWeight: 600, maxWidth: 360,
        }}
      >
        <AlertTriangle size={13} className="shrink-0" />
        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={messageFor(status, rawStatus)}>
          {messageFor(status, rawStatus)}
        </span>
        {status !== 'tenant_mismatch' && (
          <button
            type="button"
            onClick={() => setShowGetLicensed(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
              background: 'none', border: `1px solid ${styles.border}`, borderRadius: 4,
              padding: '2px 6px', cursor: 'pointer', color: styles.text, fontSize: 10, fontWeight: 700,
            }}
          >
            <KeyRound size={11} /> Get licensed
          </button>
        )}
        <button
          type="button"
          title="Dismiss for this session"
          onClick={() => { sessionStorage.setItem(LICENSE_BANNER_DISMISS_KEY, token); setDismissed(token); }}
          style={{ display: 'flex', flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: styles.text, opacity: 0.7 }}
        >
          <X size={12} />
        </button>
      </div>
      <GetLicensedModal open={showGetLicensed} onClose={() => setShowGetLicensed(false)} />
    </>
  );
}
